import type { Campaign } from './model';
import type { Material, PublicationPackage } from './campaigns';

export const ctaRulePath = 'estrategia/regras-editoriais.md';
export type CtaException = 'guia_orcamentos' | null;
export interface CtaReview {
  policyVersion: 1;
  decision: 'not_a_direct_request' | 'guide_exception';
  note: string;
  reviewedBy: string;
  reviewedAt: string;
  sourcePath: string;
}
export interface CtaAssessment {
  status: 'clear' | 'guide_exception' | 'review_required' | 'blocked';
  message: string;
  issues: { field: string; excerpt: string; intent: 'request' | 'mention'; covered: boolean }[];
}
export function guideExceptionEligible(campaign: Pick<Campaign, 'id' | 'source'>) {
  return (
    campaign.id === 'campaign:guia-orcamentos' &&
    campaign.source?.path === 'campanhas/guia-orcamentos/campanha.md'
  );
}
const normalized = (text: string) =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
/** Conservative editorial screening. Ambiguous mentions require a human statement, never an inferred violation. */
export function assessPackageCta(
  campaign: Pick<Campaign, 'id' | 'source'>,
  pkg: Pick<PublicationPackage, 'caption' | 'cta' | 'materialIds' | 'ctaException'>,
  materials: Material[],
): CtaAssessment {
  const exception = pkg.ctaException === 'guia_orcamentos' && guideExceptionEligible(campaign);
  const texts = [
    { field: 'CTA da campanha', text: pkg.cta },
    { field: 'Legenda do pacote', text: pkg.caption },
    ...materials
      .filter((material) => pkg.materialIds.includes(material.id) && material.kind !== 'midia')
      .map((material) => ({
        field: `${material.kind === 'roteiro' ? 'Roteiro' : 'Legenda'}: ${material.title}`,
        text: material.text,
      })),
  ];
  const issues: CtaAssessment['issues'] = [];
  for (const source of texts) {
    for (const excerpt of source.text
      .split(/\n+|(?<=[.!?;])\s+/)
      .map((part) => part.trim())
      .filter(Boolean)) {
      const text = normalized(excerpt);
      if (!/\b(?:direct|dm|inbox|mensage(?:m|ns) privad[ao]s?)\b/.test(text)) continue;
      const negative = /^(?:nao|nunca|evite|sem)\b/.test(text);
      const quotedOrNarrative =
        /^["'“‘`]/.test(text) ||
        /\b(?:dizia|diziam|disse|recebi|recebemos|recebia|historico|exemplo|citado|citacao)\b/.test(
          text,
        );
      const invitation =
        /\b(?:mande|manda|mandar|envie|envia|enviar|chame|chama|chamar|escreva|escrever|peca|pedir|solicite|solicitar|fale|falar|receba|ganhe|entre em contato)\b/.test(
          text,
        ) ||
        /\bguia\b.{0,50}\b(?:no|pelo|por)\s+(?:(?:meu|seu|o)\s+)?(?:direct|dm|inbox)\b/.test(text);
      const intent = invitation && !negative && !quotedOrNarrative ? 'request' : 'mention';
      const accounts = text.match(/@[a-z0-9._]+/g) ?? [];
      const covered =
        intent === 'request' &&
        exception &&
        /\bguia\b/.test(text) &&
        accounts.every((account) => account.replace(/[.]+$/, '') === '@reinaldoferreiraoficial');
      issues.push({ field: source.field, excerpt, intent, covered });
    }
  }
  if (issues.some((issue) => issue.intent === 'request' && !issue.covered))
    return {
      status: 'blocked',
      message:
        'Pedido de direct fora da exceção declarada. Revise o texto ou, na campanha oficial, declare a exceção GUIA para o direct do fundador antes de aprovar.',
      issues,
    };
  if (issues.some((issue) => issue.intent === 'mention'))
    return {
      status: 'review_required',
      message:
        'O texto menciona direct ou mensagem privada. O aprovador deve conferir a intenção e registrar por que essa menção não é um pedido de direct.',
      issues,
    };
  if (issues.length)
    return {
      status: 'guide_exception',
      message:
        'Exceção GUIA declarada para a campanha oficial: pedido no direct do @reinaldoferreiraoficial. Não exige aviso de suporte.',
      issues,
    };
  return {
    status: 'clear',
    message:
      'Nenhum pedido de direct identificado nos textos do pacote. Confira também a mídia final durante a revisão.',
    issues,
  };
}

export interface PackageDifference {
  field: string;
  label: string;
  before: string[];
  after: string[];
}
const profile = (value: string) => value.trim().replace(/^@/, '').toLowerCase();
function packageFields(pkg: PublicationPackage, materials: Material[]) {
  const destinations = pkg.structuredDestinations
    ? pkg.structuredDestinations
        .map(
          (target) =>
            `${target.channel} @${profile(target.account)} · colaboradores: ${
              target.collaborators
                .map(profile)
                .sort()
                .map((name) => '@' + name)
                .join(', ') || 'nenhum'
            }`,
        )
        .sort()
    : [...pkg.destinations].sort();
  return [
    ['caption', 'Legenda', [pkg.caption]],
    ['cta', 'CTA da campanha', [pkg.cta || 'Sem CTA']],
    [
      'ctaException',
      'Exceção de CTA',
      [
        pkg.ctaException === 'guia_orcamentos'
          ? 'GUIA no direct do fundador'
          : pkg.ctaPolicyVersion
            ? 'Nenhuma exceção'
            : 'Não declarada no pacote anterior',
      ],
    ],
    ['format', 'Formato', [pkg.contentFormat]],
    ['destinations', 'Contas e colaboradores', destinations],
    [
      'schedule',
      'Programação',
      [
        `${pkg.date || 'Sem data'}${pkg.plannedTime ? ` ${pkg.plannedTime} · ${pkg.plannedTimezone || 'America/Sao_Paulo'}` : ' · sem horário'}`,
      ],
    ],
    [
      'materials',
      'Versões dos materiais',
      [...pkg.materialIds].sort().map((id) => {
        const material = materials.find((item) => item.id === id);
        return material
          ? `${material.title} (${material.kind})\nVersão: ${id}\nSHA-256: ${material.sha256}${material.url ? `\nReferência: ${material.url}` : ''}`
          : `Material indisponível: ${id}`;
      }),
    ],
    ['editorialNote', 'Observação editorial', [pkg.editorialNote || 'Sem observação']],
  ] as const;
}
export function previousPackage(pkg: PublicationPackage, packages: PublicationPackage[]) {
  return packages
    .filter(
      (item) =>
        item.campaignId === pkg.campaignId &&
        item.contentId === pkg.contentId &&
        item.revision < pkg.revision,
    )
    .sort((a, b) => b.revision - a.revision)[0];
}
/** Uses immutable package payloads; mutable decision/status and unrelated operational events are excluded. */
export function comparePackages(
  previous: PublicationPackage,
  current: PublicationPackage,
  materials: Material[],
): PackageDifference[] {
  if (previous.campaignId !== current.campaignId || previous.contentId !== current.contentId)
    throw new Error('Compare revisões da mesma peça e campanha.');
  const before = new Map(
    packageFields(previous, materials).map(([field, , values]) => [field, values]),
  );
  return packageFields(current, materials).flatMap(([field, label, values]) =>
    JSON.stringify(before.get(field)) === JSON.stringify(values)
      ? []
      : [{ field, label, before: [...before.get(field)!], after: [...values] }],
  );
}
